import moment from 'moment'
import config from '../../config'
import { getDefaults } from '../../routes/events/state/events-query'
import actions from '../actions'
import { EVENT_VIEW_TYPES } from '../events/reducers'
import { CHANGE_PAGE } from '../events/types'
import {
    SET_USER,
    SET_CLIENT,
    SET_SECURITY_ROLES,
    SET_CONTENT_GROUPS,
    SET_WEBSOCKET,
    GET_EVENTS,
    GET_EVENT_LOG_ITEMS,
    GET_EVENTS_ANALYTICS,
    GET_ENCODERS,
    GET_FULL_EVENT,
    STARTED_REQUESTING_EVENT_LOG_ITEMS,
    FINISHED_REQUESTING_EVENT_LOG_ITEMS
} from './types'

export const setUser = payload => {
    return (dispatch, getState) => {
        dispatch({type: SET_USER, payload})
    }
}

export const setClient = payload => {
    return (dispatch, getState) => {
        dispatch({type: SET_CLIENT, payload})
    }
}

export const setSecurityRoles = payload => {
    return (dispatch, getState) => {
        dispatch({type: SET_SECURITY_ROLES, payload})
    }
}

export const setContentGroups = payload => {
    return (dispatch, getState) => {
        dispatch({ type: SET_CONTENT_GROUPS, payload })
    }
}

export const setWebsocket = payload => {
    return (dispatch, getState) => {
        if (getState().data.websocket === null) {
            // only set websocket if it hasn't been set yet
            dispatch({ type: SET_WEBSOCKET, payload})
        }
    }
}

/**
 * Remove the utc offset of dates in filters, the backend is expecting dates for the local timezone
 * Modifies the filters object date properties  directly
 * @param {Object} filters state filters
 */
const convertUtcToLocal = filters => {
    // for any date in the filters remove the timezone offset by converting to yyyy-mm-dd hh:mm:ss
    Object.keys(filters).forEach(key => {
        const value = filters[key]
        if (key === 'startDateTime' || key === 'stopDateTime') {
            filters[key] = moment(value).format('YYYY-MM-DD HH:mm:ss')
        }
    })
}

const getEventsRequestPostData = (payload, state) => {
    let filters = {...(state.events.filters || getDefaults(state))}
    if (payload.postData) {
        if (payload.merge) {
            filters = {
                ...filters,
                ...payload.postData,
            }
        } else {
            filters = payload.postData
        }
    }
    if (!filters.clientID) {
        filters.clientID = state.data.user.clientId
    }
    if (!(payload.postData && payload.postData.eventID)) {
        // remove the eventID if it was not explicitly set
        delete filters.eventID
    }
    convertUtcToLocal(filters)
    return filters
}

const sendRequestEvents = (dispatch, postData, url) => {
    if (postData.eventID || postData.customID) {
        delete postData.startDateTime
        delete postData.stopDateTime
    }
    if (postData.anyCategories && !Array.isArray(postData.anyCategories)) {
        postData.anyCategories = [postData.anyCategories]
    }
    postData.getAudioDescriptionProjectID = true
    dispatch(actions.request.sendRequest({
        type: GET_EVENTS,
        postData,
        url
    }))
}

export const requestFullEvent = eventID => {
    return (dispatch, getState) => {
        const state = getState()
        return dispatch(
            actions.request.sendRequest({
              type: GET_FULL_EVENT,
              url: `${config.uris.public}Console/getEvent`,
              postData: {
                eventID,
                clientID: state.data.user.clientId,
                getLiveMetaData: true,
                includePrivate: true,
                showEncoder: true,
                showMediaAssets: true,
                showMediaDetails: true,
                showStreams: true,
                getAudioDescriptionProjectID: true,
              },
            })
        )
    }
}

export const requestFullEvents = payload => {
    return (dispatch, getState) => {
        sendRequestEvents(dispatch, getEventsRequestPostData(payload, getState()), `${config.uris.public}Console/getEventsList`)
    }
}

export const requestSlimEvents = payload => {
    return (dispatch, getState) => {
        sendRequestEvents(dispatch, getEventsRequestPostData(payload, getState()), `${config.uris.public}Console/getEventsSlim`)
    }
}

const getShouldRequestSlim = (viewType, filters) => {
    return (viewType === EVENT_VIEW_TYPES.SLIM.key ||
        viewType === EVENT_VIEW_TYPES.GRID.key ||
        viewType === EVENT_VIEW_TYPES.CALENDAR.key) &&
      !filters.eventID
}

export const requestEvents = payload => {
    return (dispatch, getState) => {
        const state = getState()
        const filters = state.events.filters
        // to request slim events- must be in slim view - must not be viewing full event (filters include eventid)
        const viewType = state.events.viewType.key
        const shouldRequestSlim = getShouldRequestSlim(viewType, filters)
        const action = shouldRequestSlim ? requestSlimEvents : requestFullEvents
        dispatch(action({postData: state.events.filters || getDefaults(state)}))
    }
}

export const requestEventLogItems = payload => {
    return (dispatch, getState) => {
        const { eventID } = payload
        const state = getState()
        const clientID = state.data.user.clientId
        const event = state.data.events[eventID]
        const assetIDs = event.mediaAssets
          .filter((asset) => asset.type === 'video')
          .map((asset) => asset.assetID)
        // also need to make requests for each of the assets the event has
        dispatch({
            type: STARTED_REQUESTING_EVENT_LOG_ITEMS,
        })
        dispatch(actions.request.sendRequest({
            type: GET_EVENT_LOG_ITEMS,
            url: `${config.uris.crud}Indexing/getEventIndexLogItems`,
            postData: {
                clientID,
                eventID
            },
            withResult: () => {
                if (assetIDs.length) {
                    fetchAssetLogs()
                } else {
                    dispatch({type: FINISHED_REQUESTING_EVENT_LOG_ITEMS})
                }
            }
        }))
        const fetchAssetLogs = () => {
            const promises = new Promise((resolve, reject) => {
                assetIDs.forEach((assetID) => {
                    return dispatch(actions.request.sendRequest({
                        type: GET_EVENT_LOG_ITEMS,
                        url: `${config.uris.crud}Indexing/getEventIndexLogItems`,
                        postData: {
                            clientID,
                            eventID,
                            assetID
                        },
                        withResult: () => {
                            resolve()
                        }
                    }))
                })
            })
            promises.then(() => {
                dispatch({type: FINISHED_REQUESTING_EVENT_LOG_ITEMS})
            })
        }
    }
}

export const requestEventAnalytics = eventIDs => {
    return (dispatch, getState) => {
        const state = getState()
        const clientID = state.data.user.clientId
        var evSdt = state.data.events[eventIDs[0]].startDateTime
        for(var i = 0; i<eventIDs.length; i++) {
            if(moment(state.data.events[eventIDs[i]].startDateTime) < moment(evSdt)) {
                evSdt = state.data.events[eventIDs[i]].startDateTime
            }
        }
        dispatch(actions.request.sendRequest({
            type: GET_EVENTS_ANALYTICS,
            headers: {
                'wsc-api-key': config.apiAuthentication.analyticsKey,
                authorization: config.apiAuthentication.analyticsAuthentication
            },
            url: 'https://playerstats-v2.invintus.com/stats/getEventsViewerCounts',
            postData: {
                clientID,
                showCurrent: true,
                eventID: eventIDs,
                startDateTime: evSdt
            }
        }))
    }
}

export const pageChange = payload => {
    return (dispatch, getState) => {
        const { page } = payload
        dispatch({
            type: CHANGE_PAGE,
            payload: page
        })
        const state = getState()
        const shouldRequestSlim = getShouldRequestSlim(state.events.viewType.key, state.events.filters)
        const action = shouldRequestSlim ? requestSlimEvents : requestFullEvents
        dispatch(action({ merge: true, postData: { resultPage: page }}))
    }
}

export const requestEncoders = payload => {
    return (dispatch, getState) => {
        dispatch(actions.request.sendRequest({
            type: GET_ENCODERS,
            postData: {
                clientID: getState().data.user.clientId
            },
            url: `${config.uris.public}Console/getEncoders`
        }))
    }
}
